'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { Tick01Icon, Copy01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Text } from '../../../../components/styled/text';
import { Tooltip } from '../../../../components/tooltip';
import { MarkdownPreview } from '../../../markdown-preview';
import { ReadOnlyCodeEditor } from '../../components/ReadOnlyCodeEditor';
import {
  formatTruncatedFilePath,
  getReadFileEditorLanguage,
} from '../utils/read-file-tool';

type TraceReadFileContentProps = {
  filePath?: string;
  output: string;
  error?: string;
  isMarkdown: boolean;
  showPathHeader?: boolean;
  borderless?: boolean;
  maxHeight?: string;
};

export function TraceReadFileContent({
  filePath,
  output,
  error,
  isMarkdown,
  showPathHeader = true,
  borderless = false,
  maxHeight = '300px',
}: TraceReadFileContentProps) {
  const t = useTranslations('designSystem.chat');
  const [copiedContent, setCopiedContent] = useState(false);
  const copyContent = () => {
    navigator.clipboard
      .writeText(output)
      .then(() => {
        setCopiedContent(true);
        window.setTimeout(() => setCopiedContent(false), 1500);
      })
      .catch((copyError: unknown) => {
        console.error('Failed to copy read file content', copyError);
      });
  };

  if (error?.trim()) {
    return (
      <Box
        bg="bg.canvas"
        borderRadius={borderless ? '0' : 'sm'}
        borderWidth={borderless ? '0' : '1px'}
        borderColor="border.error"
        width="100%"
        overflow="hidden"
      >
        <Box px={3} py={2}>
          <Text fontSize="xs" color="fg.error" whiteSpace="pre-wrap">
            {error}
          </Text>
        </Box>
      </Box>
    );
  }

  if (!output) {
    return (
      <Box
        bg="bg.subtle"
        borderRadius={borderless ? '0' : 'sm'}
        borderWidth={borderless ? '0' : '1px'}
        borderColor="border.neutral.muted"
        width="100%"
        overflow="hidden"
      >
        <Box px={3} py={2}>
          <Text fontSize="xs" color="fg.muted" fontStyle="italic">
            {t('noOutput')}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      bg="bg.subtle"
      borderRadius={borderless ? '0' : 'sm'}
      borderWidth={borderless ? '0' : '1px'}
      borderTopWidth={borderless ? '1px' : undefined}
      borderColor="border.neutral.muted"
      width="100%"
      overflow="hidden"
    >
      {showPathHeader && filePath ? (
        <HStack
          px={3}
          py={2}
          bg="bg.muted"
          borderBottomWidth="1px"
          borderColor="border.default"
          justify="space-between"
          alignItems="center"
          gap={2}
          minW={0}
        >
          <Text
            fontSize="2xs"
            fontWeight="medium"
            color="fg.muted"
            fontFamily="mono"
            truncate
            minW={0}
            flex={1}
          >
            {formatTruncatedFilePath({ filePath })}
          </Text>
          <Tooltip title={t('readTool.copyContent')}>
            <Box
              as="button"
              aria-label={t('readTool.copyContent')}
              onClick={copyContent}
              h="5"
              w="5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              color="fg.muted"
              borderRadius="xs"
              flexShrink={0}
              _hover={{
                color: 'fg.default',
                bg: 'bg.subtle',
              }}
            >
              {copiedContent ? (
                <Tick01Icon size={12} />
              ) : (
                <Copy01Icon size={12} />
              )}
            </Box>
          </Tooltip>
        </HStack>
      ) : null}

      <Box maxH={maxHeight} overflow="auto">
        {isMarkdown ? (
          <Box px={3} py={2}>
            <MarkdownPreview source={output} fontSize={12} compact />
          </Box>
        ) : (
          <ReadOnlyCodeEditor
            value={output}
            maxHeight={maxHeight}
            minHeight="44px"
            showLineNumbers
            showCopyButton={false}
            borderless
            language={getReadFileEditorLanguage(filePath)}
          />
        )}
      </Box>
    </Box>
  );
}
